import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

// ---- Nav Items ----
const navItems = [
  { key: "nav.home", path: "/", icon: <HomeIcon fontSize="small" /> },
  { key: "nav.about", path: "/about" },
  { key: "nav.services", path: "/services" },
  { key: "nav.contact", path: "/contact" },
  { key: "nav.faq", path: "/faq" },
];

// ---- Language Button Component ----
const LanguageSwitchButton = ({ language, toggle }) => (
  <Button
    onClick={toggle}
    sx={{
      ml: 1,
      border: "1px solid #1976d2",
      color: "#1976d2",
      borderRadius: 20,
      px: 2,
      textTransform: "none",
      fontWeight: "bold",
      "&:hover": {
        backgroundColor: "rgba(25,118,210,0.05)",
      },
    }}
  >
    {language === "ro" ? "EN" : "RO"}
  </Button>
);

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t, i18n } = useTranslation();


  useEffect(() => {
  const savedLang = localStorage.getItem("language");
  if (savedLang && savedLang !== i18n.language) {
    i18n.changeLanguage(savedLang);
  }
}, [i18n]); // ✅ referință stabilă, nu cauzează re-run-uri

  const toggleLanguage = () => {
    const newLang = i18n.language === "ro" ? "en" : "ro";
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
  };

  const toggleDrawer = () => {
    setMobileOpen(!mobileOpen);
  };

  // ---- Drawer Content ----
  const drawer = (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        sx={{
          textAlign: "center",
          p: 2,
          backgroundColor: "rgba(255,255,255,0.75)",
          backdropFilter: "blur(15px)",
          height: "100vh",
          borderTopLeftRadius: 20,
          borderBottomLeftRadius: 20,
        }}
      >
        <Typography variant="h6" sx={{ my: 2, fontWeight: 700, color: "#1976d2" }}>
          {t("nav.drawerTitle")}
        </Typography>

        <List>
          {navItems.map((item) => (
            <ListItem key={item.key} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                sx={{
                  textAlign: "center",
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "rgba(25,118,210,0.05)",
                  },
                }}
                onClick={toggleDrawer}
              >
                <ListItemText primary={t(item.key)} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <LanguageSwitchButton
          language={i18n.language}
          toggle={toggleLanguage}
        />
      </Box>
    </motion.div>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: "rgba(255,255,255,0.75)",
          backdropFilter: "blur(15px)",
          color: "#0e4379",
          borderBottomLeftRadius: 20,
          borderBottomRightRadius: 20,
          py: 1.5,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          {/* ---- Logo ---- */}
          <motion.div whileHover={{ scale: 1.03 }} transition={{ type: "spring" }}>
            <Link
              to="/"
              style={{ display: "flex", alignItems: "center", textDecoration: "none" }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "1px",
                  background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Advanced <span style={{ color: "#0e4379" }}>Analytics</span> Solutions
              </Typography>
            </Link>
          </motion.div>

          {/* ---- Desktop Menu ---- */}
          <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1 }}>
            {navItems.map((item) => (
              <Button
                key={item.key}
                component={Link}
                to={item.path}
                sx={{
                  color: "#0e4379",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.95rem",
                  px: 2,
                  borderRadius: 20,
                  "&:hover": { backgroundColor: "rgba(25,118,210,0.05)" },
                  display: "flex",
                  gap: item.icon ? 1 : 0,
                }}
              >
                {item.icon && item.icon}
                {t(item.key)}
              </Button>
            ))}

            <LanguageSwitchButton
              language={i18n.language}
              toggle={toggleLanguage}
            />
          </Box>

          {/* ---- Mobile Menu Button ---- */}
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer}
            sx={{ display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* ---- Mobile Drawer ---- */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={toggleDrawer}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { background: "transparent", boxShadow: "none" } }}
      >
        {drawer}
      </Drawer>
    </>
  );
}

export default Navbar;
